import axios, {
  AxiosError,
  type AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { HOST_URL } from "config";
import { PATH } from "routes/paths";
import type { CustomResponseFormat } from "api/instance";

// 앰버서더(인플루언서) 대시보드 API.
// 앰버서더 토큰은 유저 인증(X-Auth-Token/유저 Bearer)과 완전 별개 체계라 기존 두 인스턴스를 쓰지 않는다 —
// 각 인스턴스의 401 처리(/login·/promotion-login 리다이렉트, 토큰 리프레쉬)가 앰버서더 플로우와 맞지 않기 때문.

// 키 문자열은 유지한다 — 바꾸면 기존 로그인 세션이 전부 로그아웃된다.
export const AMBASSADOR_TOKEN_STORAGE_KEY = "AMBASSADOR_TOKEN";

export const getAmbassadorToken = () =>
  localStorage.getItem(AMBASSADOR_TOKEN_STORAGE_KEY);
export const setAmbassadorToken = (token: string) =>
  localStorage.setItem(AMBASSADOR_TOKEN_STORAGE_KEY, token);
export const clearAmbassadorToken = () =>
  localStorage.removeItem(AMBASSADOR_TOKEN_STORAGE_KEY);

export const AMBASSADOR_DOMAIN = "ambassador";

export const AMBASSADOR_SIGNUP_ENDPOINT = "signup";
export const AMBASSADOR_VERIFY_ENDPOINT = "verify";
export const AMBASSADOR_LOGIN_ENDPOINT = "login";
export const AMBASSADOR_RESEND_ENDPOINT = "verification/resend";
export const AMBASSADOR_DASHBOARD_ENDPOINT = "dashboard";
export const AMBASSADOR_PASSWORD_CHANGE_ENDPOINT = "password/change";
export const AMBASSADOR_PASSWORD_RESET_REQUEST_ENDPOINT =
  "password/reset/request";
export const AMBASSADOR_PASSWORD_RESET_ENDPOINT = "password/reset";
export const AMBASSADOR_CONTRACT_ENDPOINT = "contract";
export const AMBASSADOR_CONTRACT_SIGN_ENDPOINT = "contract/sign";
export const AMBASSADOR_CONTRACT_SIGN_PDF_ENDPOINT = "contract/sign/pdf";
export const AMBASSADOR_CONTRACT_SIGN_PREVIEW_ENDPOINT =
  "contract/sign/preview";
export const AMBASSADOR_CONTRACT_DOCUMENTS_ENDPOINT = "contract/documents";
export const AMBASSADOR_CODE_CREATE_ENDPOINT = "code/create";
export const AMBASSADOR_NAME_ENDPOINT = "name";
export const AMBASSADOR_TRIAL_ENDPOINT = "trial";
export const AMBASSADOR_TRIAL_REQUEST_ENDPOINT = "trial-request";
export const AMBASSADOR_POST_ENDPOINT = "post";
export const AMBASSADOR_POST_CREATE_ENDPOINT = "post/create";
export const AMBASSADOR_POST_DELETE_ENDPOINT = "post/delete";

export type AmbassadorTokenResult = {
  accessToken: string;
  // 활동명 (계약 제출 전이면 null)
  name?: string | null;
};

export type AmbassadorStatsSummary = {
  signupCount: number;
  payerCount: number;
  refundCount: number;
  // 정산액(원) — 그 달 적용 수수료(결제자 1인당 정액)가 있는 monthly/total 에만 채워진다. daily 는 항상 null.
  settlementAmount?: number | null;
  // 그 달 정산이 확정됐는지 (계약서 제4조 ⑤, 익월 8일). monthly 버킷에만 의미가 있다.
  // 확정된 달은 이후 환불에도 금액이 바뀌지 않고, 확정 전이면 아직 달라질 수 있는 예상치다.
  confirmed?: boolean;
};

// period 는 daily 면 "2026-08-03", monthly 면 "2026-08" (서버 최신순 정렬)
export type AmbassadorStatsBucket = AmbassadorStatsSummary & {
  period: string;
};

// 가입자 = 링크로 유입되어 코드가 귀속된 유저, 결제자 = 정산 기여 확정 유저, 환불자 = 그중 결제 취소 유저
export type AmbassadorDashboardResult = {
  // 활동명 (계약 제출 전이면 null)
  name?: string | null;
  // 로그인 계정 전화번호, 숫자만 "01012345678" (계정 관리 메뉴 표시·계약 폼 프리필용)
  phoneNumber?: string | null;
  codes: string[];
  // 코드별 승인(활성) 상태 — 미승인 코드는 링크 복사를 막고 승인 대기로 표시
  codeInfos?: { code: string; isActive: boolean }[];
  total: AmbassadorStatsSummary;
  daily: AmbassadorStatsBucket[];
  monthly: AmbassadorStatsBucket[];
};

// 계약형태 — 발송할 계약서 폼 구분 (서버 AmbassadorContractTypeConstants 와 페어)
export type AmbassadorContractType =
  | "freelancer"
  | "business_general"
  | "business_simplified";

// 계약 정보 — type 이 null 이면 미제출. 파일은 base64 data URL 원문.
// name(활동명)은 링크(referral_code.name)에서 온다 — 링크를 만들기 전이면 비어 있다.
export type AmbassadorContractResult = {
  // 계약 완료 여부 (어드민이 체결 확인 후 표시) — 완료 전에는 셀프 링크를 만들 수 없다
  completed?: boolean;
  type?: AmbassadorContractType | null;
  realName?: string | null;
  businessName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  name?: string | null;
  bankbookFile?: string | null;
  // 업로드 당시의 원본 파일명 — null 이면 화면이 규칙 이름으로 대체한다
  bankbookFileName?: string | null;
  businessRegistrationFile?: string | null;
  businessRegistrationFileName?: string | null;
  // 체결된 계약서 사본 — 어드민이 계약 완료 처리 시 업로드 (계약 당사자 다운로드·미리보기용)
  contractFile?: string | null;
  contractFileName?: string | null;
  // 전자계약 체결 시각 (ISO) — null 이면 미체결. 상태 칩(체결하기/서류 제출/승인 대기) 판별용
  signedAt?: string | null;
  // 서류 제출 단계 동의 섹션 (세무 처리·개인정보 수집) — 체결된 계약형태 기준, 미제출 계정은 빈 리스트
  documentConsents?: AmbassadorContractAgreementSection[];
  // 제출한 정산 정보 (본인 확인·수정용). 계좌는 원문, 주민등록번호는 마스킹만 내려온다
  bankName?: string | null;
  bankAccount?: string | null;
  accountHolderName?: string | null;
  // 예: "900101-1******" — 표시 전용. 변경은 전체 재입력으로만 한다 (빈 값으로 제출하면 기존 값 유지)
  residentRegistrationNumberMasked?: string | null;
  // 서류 제출 단계 동의를 이미 받았는지 — true 면 재제출 화면에서 동의를 다시 받지 않는다
  documentsAgreed?: boolean;
  // 정산 정보·서류 제출 시각 (ISO, 재제출 시 갱신)
  documentsSubmittedAt?: string | null;
};

// 전자계약 병합 미리보기 요청 — 계약서에 기재될 당사자 특정 정보만 보낸다.
// 주민등록번호·계좌번호는 계약서에 기재하지 않으며(최소수집) 서류 제출 단계에서 정산 정보로 별도 수집한다.
// 활동명은 계약서 기재 대상이 아니라 링크 생성 시점에 받는다. 연락처는 계정의 인증된 전화번호를 서버가 채운다.
export type AmbassadorContractSignPreviewParam = {
  type: AmbassadorContractType;
  realName?: string | null;
  businessName?: string | null;
  representativeName?: string | null;
  businessRegistrationNumber?: string | null;
  address: string;
  email: string;
};

// 동의 섹션 (서류 제출 단계) — key 는 서류 제출 파라미터의 agreed* 필드와 페어 (tax / privacy)
export type AmbassadorContractAgreementSection = {
  key: string;
  title: string;
  items: string[];
};

// 핵심 내용 섹션 — headline 은 크게 보여줄 한 줄 핵심(숫자·기간), items 는 보조 요점
export type AmbassadorContractKeyTermSection = {
  key: string;
  title: string;
  headline?: string | null;
  items?: string[];
};

// 병합 미리보기 응답 — content 는 당사자 정보가 병합된 계약서 전문(마크다운).
// 체결 시 동일 입력이면 동일 전문이 서버에 스냅샷으로 저장된다 (본 것 = 저장되는 것).
// keyTerms 는 체결 전 핵심 내용 확인 단계에서 보여줄 정리 (템플릿 버전 단위로 서버가 관리)
export type AmbassadorContractSignPreviewResult = {
  templateVersion: string;
  templateHash: string;
  content: string;
  keyTerms?: AmbassadorContractKeyTermSection[];
};

// 전자계약 체결 요청 — 미리보기 정보 + 열람한 템플릿 버전(갱신 방어) + 핵심 내용·전문 동의.
// 세무 처리·개인정보 동의는 서류 제출 단계에서 받는다. 계약서 전문은 보내지 않는다 — 서버가 재병합해 저장한다.
export type AmbassadorContractSignParam = AmbassadorContractSignPreviewParam & {
  templateVersion: string;
  // 핵심 내용(계약 기간·수수료·유효 결제·정산·콘텐츠 의무·주의) 확인
  agreedKeyTerms: boolean;
  // 전문 확인·체결 포괄 동의 (전문 열람 후 최종 동의)
  agreedFull: boolean;
};

// 체결본 조회 응답 — signed=false 면 나머지는 null. content 는 저장된 스냅샷 전문(마크다운).
export type AmbassadorContractSignResult = {
  signed: boolean;
  signedAt?: string | null;
  templateVersion?: string | null;
  snapshotHash?: string | null;
  content?: string | null;
};

// 정산 정보·서류 제출 — 전자서명 이후, 계약 완료(어드민 승인) 전까지 재제출(덮어쓰기) 허용.
export type AmbassadorContractDocumentsParam = {
  // 프리랜서 필수 (원천징수·지급명세서 제출용, 하이픈 허용)
  residentRegistrationNumber?: string | null;
  bankName: string;
  bankAccount: string;
  accountHolderName: string;
  bankbookFile: string;
  bankbookFileName?: string | null;
  // 사업자 필수 (기존 제출본이 있으면 생략 가능)
  businessRegistrationFile?: string | null;
  businessRegistrationFileName?: string | null;
  // 세무 처리 확인 (프리랜서 원천징수 / 사업자 세금계산서) — 필수
  agreedTax: boolean;
  // 개인정보 수집·이용 동의 — 정산 정보(주민번호·계좌) 수집 직전에 받는다, 필수
  agreedPrivacy: boolean;
};

// 체험용 계정 상태 — 신청 전(requested·completed 모두 false) → 확인 중(requested) → 세팅 완료(completed)
export type AmbassadorTrialResult = {
  // 신청(또는 세팅)된 회원번호
  trialUserId?: number | null;
  // 신청 시 입력한 닉네임
  nickname?: string | null;
  // 신청 접수됨 — 담당자(어드민) 확인 대기 중
  requested?: boolean;
  // 어드민이 체험 설정을 완료함
  completed?: boolean;
  // 체험 이용 종료됨 (취소 또는 만료) — completed 이면서 구독이 활성이 아닌 상태
  ended?: boolean;
};

// 등록한 홍보 게시물 1건. platform 은 서버가 링크 도메인에서 파생한 표기다.
export type AmbassadorPostDetail = {
  postId: number;
  url: string;
  // "인스타그램" | "유튜브"
  platform: string;
  // 등록일 (yyyy-MM-dd) — 게시일이 아니라 링크를 등록한 날이다
  createdDate?: string | null;
};

// 게시물 등록 현황 — 계약 제3조의 "계약 기간 내 3건 이상 게시" 진행도.
// 미달성이어도 막히는 건 없다. 진행도와 남은 기간만 보여준다.
export type AmbassadorPostResult = {
  // 계약상 필수 건수 (서버 상수)
  requiredCount: number;
  posts: AmbassadorPostDetail[];
  // 계약 기간이 끝났는지 — 끝났으면 등록 입력과 안내를 거둔다.
  // 남은 일수는 내려받지 않는다 (카운트다운은 재촉으로 읽힌다)
  ended?: boolean;
};

// 계약 정보 제출 — 폼 전체 상태 저장 (null 필드 = 비움). 형태별 필수값은 서버가 검증한다.
interface AmbassadorInstance extends AxiosInstance {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
}

export const ambassadorAxios: AmbassadorInstance = axios.create({
  baseURL: HOST_URL,
  timeout: 15000, // 15초 타임아웃
});

// 매 요청 시점에 localStorage 를 읽어 토큰을 부착한다 (로그인 직후/다른 탭 갱신에 의한 stale 방지).
const onRequest = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  const token = getAmbassadorToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

// 비즈니스 오류는 HTTP 200 + code≠0 이므로 envelope 를 그대로 반환한다 — code 검사는 호출부 책임.
const onResponse = (response: AxiosResponse): AxiosResponse => {
  // Blob 응답(체결본 PDF)은 Content-Disposition 의 파일명이 필요해 응답 전체를 넘긴다.
  // 나머지는 지금까지처럼 본문만 넘겨 호출처가 CodeResponse 를 그대로 받게 한다.
  if (response.data instanceof Blob) return response;
  return response.data;
};

// 앰버서더 토큰 무효/만료(HTTP 401)면 토큰을 지우고 로그인으로 보낸다. 로그인 페이지에서는 리다이렉트하지 않는다(루프 방지).
// 하드 리다이렉트라 라우터 state 를 못 쓰므로 원래 위치를 ?returnTo= 로 실어 로그인 후 복귀하게 한다.
const onRejected = (error: AxiosError) => {
  if (error.response?.status === 401) {
    clearAmbassadorToken();
    if (window.location.pathname !== PATH.ambassadorLogin) {
      const returnTo = encodeURIComponent(
        window.location.pathname + window.location.search
      );
      window.location.href = `${PATH.ambassadorLogin}?returnTo=${returnTo}`;
    }
  }
  return Promise.reject(error);
};

ambassadorAxios.interceptors.request.use(onRequest);
ambassadorAxios.interceptors.response.use(onResponse, onRejected);

export const ambassadorApi = {
  // 오픈 가입 (전화번호는 숫자만 "01012345678") — 성공 시 알림톡으로 인증번호 발송
  signup: (phoneNumber: string, password: string) =>
    ambassadorAxios.post<CustomResponseFormat<void>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_SIGNUP_ENDPOINT}`,
      { phoneNumber, password }
    ),

  // 전화번호 인증 — 성공 시 토큰 발급(자동 로그인).
  // 비밀번호를 함께 재검증한다 (미인증 계정을 제3자 signup 이 덮어써 탈취하는 것 차단 — 서버 정책).
  verify: (phoneNumber: string, password: string, code: string) =>
    ambassadorAxios.post<CustomResponseFormat<AmbassadorTokenResult>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_VERIFY_ENDPOINT}`,
      {
        phoneNumber,
        password,
        code,
      }
    ),

  login: (phoneNumber: string, password: string) =>
    ambassadorAxios.post<CustomResponseFormat<AmbassadorTokenResult>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_LOGIN_ENDPOINT}`,
      {
        phoneNumber,
        password,
      }
    ),

  resendVerification: (phoneNumber: string) =>
    ambassadorAxios.post<CustomResponseFormat<void>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_RESEND_ENDPOINT}`,
      { phoneNumber }
    ),

  dashboard: () =>
    ambassadorAxios.get<CustomResponseFormat<AmbassadorDashboardResult>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_DASHBOARD_ENDPOINT}`
    ),

  // 로그인 상태에서 비밀번호 변경 — 현재 비밀번호 재검증 (계정 관리 메뉴)
  changePassword: (currentPassword: string, newPassword: string) =>
    ambassadorAxios.post<CustomResponseFormat<void>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_PASSWORD_CHANGE_ENDPOINT}`,
      { currentPassword, newPassword }
    ),

  // 비밀번호 재설정 1단계 (분실 복구) — 가입(인증 완료) 전화번호로 알림톡 인증번호 발송, 30초 쿨다운
  requestPasswordReset: (phoneNumber: string) =>
    ambassadorAxios.post<CustomResponseFormat<void>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_PASSWORD_RESET_REQUEST_ENDPOINT}`,
      { phoneNumber }
    ),

  // 비밀번호 재설정 2단계 — 인증번호 대조 후 교체, 성공 시 토큰 발급(자동 로그인)
  resetPassword: (phoneNumber: string, code: string, newPassword: string) =>
    ambassadorAxios.post<CustomResponseFormat<AmbassadorTokenResult>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_PASSWORD_RESET_ENDPOINT}`,
      { phoneNumber, code, newPassword }
    ),

  // 계약 정보 조회 — type 이 null 이면 미제출
  getContract: () =>
    ambassadorAxios.get<CustomResponseFormat<AmbassadorContractResult>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_CONTRACT_ENDPOINT}`
    ),

  // 전자계약 병합 미리보기 — 당사자 정보를 병합한 계약서 전문을 받는다 (체결 시 동일 전문이 저장됨)
  previewContractSign: (param: AmbassadorContractSignPreviewParam) =>
    ambassadorAxios.post<
      CustomResponseFormat<AmbassadorContractSignPreviewResult>
    >(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_CONTRACT_SIGN_PREVIEW_ENDPOINT}`,
      param
    ),

  // 전자계약 체결 — 항목별 동의 후 서버가 병합·저장한 불변 스냅샷을 기록한다 (재서명 불가)
  signContract: (param: AmbassadorContractSignParam) =>
    ambassadorAxios.post<CustomResponseFormat<void>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_CONTRACT_SIGN_ENDPOINT}`,
      param
    ),

  // 전자계약 체결본 조회 — 저장된 스냅샷 전문 (열람·다운로드 재현용)
  getContractSign: () =>
    ambassadorAxios.get<CustomResponseFormat<AmbassadorContractSignResult>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_CONTRACT_SIGN_ENDPOINT}`
    ),

  // 체결본 PDF 다운로드 — 서버가 스냅샷 전문을 계약서 양식(A4)으로 렌더링한다.
  // 정상 응답은 PDF Blob, 미체결 등 비즈니스 오류는 JSON Blob 으로 온다 — type 검사는 호출부 책임.
  // 응답 전체를 돌려준다 — 다운로드 파일명(Content-Disposition)을 호출처가 읽어야 한다.
  getContractSignPdf: () =>
    ambassadorAxios.get<AxiosResponse<Blob>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_CONTRACT_SIGN_PDF_ENDPOINT}`,
      { responseType: "blob" }
    ),

  // 정산 정보·서류 제출 — 전자서명 이후, 계약 완료 전까지 재제출 허용
  submitContractDocuments: (param: AmbassadorContractDocumentsParam) =>
    ambassadorAxios.post<CustomResponseFormat<void>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_CONTRACT_DOCUMENTS_ENDPOINT}`,
      param
    ),

  // 레퍼럴 링크 생성 — 슬러그는 영문 소문자·숫자·하이픈 3~20자, 1인 1코드.
  // 활동명(링크 노출 이름)을 함께 받는다 — 활동명이 처음 정해지는 지점이다.
  createCode: (code: string, name: string) =>
    ambassadorAxios.post<CustomResponseFormat<void>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_CODE_CREATE_ENDPOINT}`,
      { code, name }
    ),

  // 활동명 수정 — 링크 관리 메뉴에서 본인 코드들의 노출 이름을 갱신한다
  updateName: (name: string) =>
    ambassadorAxios.post<CustomResponseFormat<void>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_NAME_ENDPOINT}`,
      { name }
    ),

  // 체험용 계정 상태 조회 — 신청 전 / 확인 중(담당자 확인 대기) / 세팅 완료
  getTrial: () =>
    ambassadorAxios.get<CustomResponseFormat<AmbassadorTrialResult>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_TRIAL_ENDPOINT}`
    ),

  // 체험용 계정 신청 — 회원번호(Long)+닉네임. 서버가 회원 존재·닉네임 일치를 검증하고 접수한다 (계약 완료 필요)
  requestTrial: (trialUserId: number, nickname: string) =>
    ambassadorAxios.post<CustomResponseFormat<void>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_TRIAL_REQUEST_ENDPOINT}`,
      { trialUserId, nickname }
    ),

  // 게시물 등록 현황 조회 — 필수 건수·등록 목록·계약 종료까지 남은 일수
  getPosts: () =>
    ambassadorAxios.get<CustomResponseFormat<AmbassadorPostResult>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_POST_ENDPOINT}`
    ),

  // 게시물 등록 — 본인 인스타그램·유튜브 링크만. 형식·중복 판정은 서버가 한다 (계약 완료 필요).
  // 응답이 갱신된 현황이라 따로 재조회하지 않는다 — 재조회는 reader 리플리카를 쳐서
  // 복제 지연 동안 방금 등록한 건이 빠져 보인다.
  createPost: (url: string) =>
    ambassadorAxios.post<CustomResponseFormat<AmbassadorPostResult>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_POST_CREATE_ENDPOINT}`,
      { url }
    ),

  // 게시물 삭제 — 잘못 넣은 링크를 고칠 수 있게. 본인 등록분만 지워지고, 갱신된 현황을 돌려준다
  deletePost: (postId: number) =>
    ambassadorAxios.post<CustomResponseFormat<AmbassadorPostResult>>(
      `${AMBASSADOR_DOMAIN}/${AMBASSADOR_POST_DELETE_ENDPOINT}`,
      { postId }
    ),
};
